import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { gql } from '@apollo/client';

const LIST_MY_MESSAGES = gql`
  query ListMyMessages($limit: Int) {
    listMyMessages(limit: $limit) {
      items {
        id
        conversationId
        clientId
        staffId
        senderId
        recipientId
        content
        isRead
        createdAt
      }
    }
  }
`;

const LIST_CASES = gql`
  query ListCases($limit: Int) {
    listCases(limit: $limit) {
      items {
        id
        caseNumber
        clientId
        client {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const SEND_MESSAGE = gql`
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      id
      conversationId
      clientId
      staffId
      senderId
      recipientId
      content
      isRead
      isEdited
      createdAt
    }
  }
`;

interface MessageItem {
  id: string;
  conversationId: string;
  clientId: string;
  staffId?: string;
  senderId: string;
  recipientId: string;
  content: string;
  isRead: boolean;
  createdAt: string;
}

interface CaseItem {
  id: string;
  caseNumber: string;
  clientId: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ListMyMessagesData {
  listMyMessages: {
    items: MessageItem[];
  };
}

interface ListCasesData {
  listCases: {
    items: CaseItem[];
  };
}

interface Conversation {
  clientId: string;
  clientName: string;
  caseId: string;
  caseNumber: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  isFromClient: boolean;
}

export function MessagesWidget() {
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState<{
    clientId: string;
    clientName: string;
    caseId: string;
  } | null>(null);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: messagesData, refetch: refetchMessages, loading: messagesLoading, error: messagesError } = useQuery<ListMyMessagesData>(LIST_MY_MESSAGES, {
    variables: { limit: 50 },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all', // Don't crash on errors, just show empty state
  });

  const { data: casesData, loading: casesLoading, error: casesError } = useQuery<ListCasesData>(LIST_CASES, {
    variables: { limit: 100 },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all', // Don't crash on errors
  });

  // Log errors for debugging
  if (messagesError) {
    console.error('Messages query error:', messagesError);
  }
  if (casesError) {
    console.error('Cases query error:', casesError);
  }

  const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE, {
    onCompleted: () => {
      setMessageText('');
      setSelectedClient(null);
      refetchMessages();
    },
  });

  // Group messages by client
  const conversations: Conversation[] = [];
  const clientMap = new Map<string, Conversation>();

  if (messagesData?.listMyMessages?.items && casesData?.listCases?.items) {
    // Sort messages by createdAt (oldest first) so we process them in order
    const sortedMessages = [...messagesData.listMyMessages.items].sort((a: any, b: any) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    sortedMessages.forEach((msg: any) => {
      if (!clientMap.has(msg.clientId)) {
        // Find the case for this client
        const clientCase = casesData.listCases.items.find(
          (c: any) => c.clientId === msg.clientId
        );

        if (clientCase) {
          clientMap.set(msg.clientId, {
            clientId: msg.clientId,
            clientName: clientCase.client
              ? `${clientCase.client.firstName} ${clientCase.client.lastName}`
              : 'Unknown Client',
            caseId: clientCase.id,
            caseNumber: clientCase.caseNumber,
            lastMessage: msg.content,
            lastMessageTime: new Date(msg.createdAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            }),
            unreadCount: 0,
            isFromClient: msg.senderId === msg.clientId,
          });
        }
      } else {
        // Update with latest message (since messages are sorted, this will always be newer)
        const conv = clientMap.get(msg.clientId);
        if (conv) {
          conv.lastMessage = msg.content;
          conv.lastMessageTime = new Date(msg.createdAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          });
          conv.isFromClient = msg.senderId === msg.clientId;
        }
      }

      // Count unread messages from client
      if (!msg.isRead && msg.senderId === msg.clientId) {
        const conv = clientMap.get(msg.clientId);
        if (conv) conv.unreadCount++;
      }
    });

    conversations.push(...Array.from(clientMap.values()));
  }

  // Filter conversations by search query
  const filteredConversations = conversations.filter((conv) =>
    conv.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.caseNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSendQuickMessage = async () => {
    if (!selectedClient || !messageText.trim()) return;

    try {
      await sendMessage({
        variables: {
          input: {
            clientId: selectedClient.clientId,
            recipientId: selectedClient.clientId,
            content: messageText.trim(),
          },
        },
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleViewFullChat = (conversation: Conversation) => {
    navigate(`/cases/${conversation.caseId}`, { state: { openMessagesTab: true } });
  };

  return (
    <Card className="h-[600px] flex flex-col">
      <CardHeader className="border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">💬</span>
            <CardTitle>Messages</CardTitle>
            {conversations.length > 0 && (
              <span className="text-sm text-gray-500">({conversations.length})</span>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/messages')}
          >
            View All
          </Button>
        </div>

        {/* Search Bar */}
        <div className="relative mt-4">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</span>
          <Input
            placeholder="Search clients or cases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden p-0 flex flex-col">
        {/* Quick Message Composer */}
        {selectedClient && (
          <div className="border-b bg-gray-50 p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-semibold text-sm">{selectedClient.clientName}</p>
                <p className="text-xs text-gray-500">Quick Message</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedClient(null);
                  setMessageText('');
                }}
              >
                ✕
              </Button>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Type your message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendQuickMessage();
                  }
                }}
                className="flex-1"
              />
              <Button
                onClick={handleSendQuickMessage}
                disabled={!messageText.trim() || sending}
                size="sm"
              >
                📤
              </Button>
            </div>
          </div>
        )}

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {messagesLoading || casesLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red mb-4"></div>
              <p className="text-gray-500">Loading messages...</p>
            </div>
          ) : messagesError || casesError ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <span className="text-6xl mb-4">⚠️</span>
              <p className="text-red-600 font-medium">Error loading messages</p>
              <p className="text-sm text-gray-500 mt-2">
                {messagesError?.message || casesError?.message || 'Unknown error'}
              </p>
              <Button
                onClick={() => {
                  refetchMessages();
                }}
                variant="outline"
                size="sm"
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <span className="text-6xl mb-4">💬</span>
              <p className="text-gray-500 font-medium">
                {searchQuery ? 'No conversations found' : 'No messages yet'}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {searchQuery
                  ? 'Try a different search term'
                  : 'Start messaging clients from their case pages'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.clientId}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleViewFullChat(conversation)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate">
                          {conversation.clientName}
                        </p>
                        {conversation.unreadCount > 0 && (
                          <span className="bg-aalaw-red text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        Case #{conversation.caseNumber}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 ml-2">
                      {conversation.lastMessageTime}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 truncate mb-2">
                    {conversation.isFromClient && (
                      <span className="font-medium text-aalaw-red">Client: </span>
                    )}
                    {conversation.lastMessage}
                  </p>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedClient({
                          clientId: conversation.clientId,
                          clientName: conversation.clientName,
                          caseId: conversation.caseId,
                        });
                      }}
                      className="text-xs"
                    >
                      Quick Reply
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewFullChat(conversation);
                      }}
                      className="text-xs"
                    >
                      View Chat
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
